import React, { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { Box, Button, Typography } from '@mui/material';
import { ButtonGroupWrapper } from '@sharedCR/coreDesignSystem/Buttons';
import { ModalSize } from 'app/shared/componentsRestyled/coreDesignSystem/Modals';
import clsx from 'clsx';
import withLoading, { WithLoadingPropTypes } from 'hocs/withLoading';
import PropTypes from 'prop-types';

import { COMPOSED_CLAIMS } from 'constants/composedClaims';
import { HORIZONTAL_ALIGNMENT } from 'constants/ui';

import { selectIsClaimAllowed } from 'store/auth/selectors';
import { closeConfirmationDialogAction, openDialogAction } from 'store/global/actions';
import { selectIsProgramChangeDisabled } from 'store/loanApplications/selectors';

import ChooseProgramPopup from '../ChooseProgramPopup';

import { UpdateProgramPopupTestIds } from './UpdateProgramPopup.testIds';

const UpdateProgramPopup = ({
  onAccept,
  onRevert,
  isRevertNeededAfterAccept = true,
  isSuccessActionNeededAfterAccept = true,
  isSaveWithoutProgramHidden = false,
  handlerProps,
  message,
  isLoading,
  setIsLoading,
  loanOptionsSource,
}) => {
  const dispatch = useDispatch();
  const isSaveWithoutProgramAllowed = useSelector(
    selectIsClaimAllowed(COMPOSED_CLAIMS.LOAN_APPLICATION_SAVE_WITHOUT_PROGRAM),
  );
  const isChangeProgramAllowed = useSelector(
    selectIsClaimAllowed(COMPOSED_CLAIMS.LOAN_APPLICATION_CHANGE_PROGRAM),
  );
  const isProgramChangeDisabled = useSelector(selectIsProgramChangeDisabled);

  const isSaveWithoutProgram = isSaveWithoutProgramAllowed && !isSaveWithoutProgramHidden;

  const onSaveWithoutProgramClick = useCallback(() => {
    setIsLoading(true);

    onAccept({ saveWithoutProgram: true })
      .then(() => dispatch(closeConfirmationDialogAction()))
      .finally(() => setIsLoading(false));
  }, [dispatch, onAccept, setIsLoading]);

  const onSaveWithProgramClick = useCallback(() => {
    dispatch(closeConfirmationDialogAction());

    dispatch(
      openDialogAction({
        header: 'Confirm Lock Rate',
        size: ModalSize.FULL,
        customButtons: <div className="w-full" id="choose-program-popup-controls" />,
        onAcceptButtonName: 'Change Program',
        hasScrollY: true,
        isWithDivider: true,
        body: () => (
          <ChooseProgramPopup
            isProgramReselection
            handlerProps={handlerProps}
            isFromPropertyAndLoan={false}
            isRevertNeededAfterAccept={isRevertNeededAfterAccept}
            isSuccessActionNeededAfterAccept={isSuccessActionNeededAfterAccept}
            loanOptionsSource={loanOptionsSource}
            onAccept={onAccept}
            onRevert={onRevert}
          />
        ),
      }),
    );
  }, [
    dispatch,
    onAccept,
    isSuccessActionNeededAfterAccept,
    onRevert,
    isRevertNeededAfterAccept,
    handlerProps,
    loanOptionsSource,
  ]);

  const onRevertClick = useCallback(() => {
    dispatch(closeConfirmationDialogAction());

    onRevert();
  }, [dispatch, onRevert]);

  return (
    <Box className="flex flex-col items-center gap-24 w-full">
      <Typography className="text-center" component="h5" variant="bold16">
        {message}
      </Typography>

      <ButtonGroupWrapper alignment={HORIZONTAL_ALIGNMENT.CENTER} className="w-full">
        <Button
          className={clsx(!(isSaveWithoutProgram && isChangeProgramAllowed) && 'flex-1')}
          data-testid={UpdateProgramPopupTestIds.CANCEL_BUTTON}
          size="large"
          variant="line"
          onClick={onRevertClick}
        >
          Cancel
        </Button>

        {isSaveWithoutProgram && (
          <Button
            className="flex-1 whitespace-nowrap"
            data-testid={UpdateProgramPopupTestIds.SAVE_WITHOUT_PROGRAM_BUTTON}
            disabled={isLoading || isProgramChangeDisabled}
            size="large"
            onClick={onSaveWithoutProgramClick}
          >
            Save Without Program
          </Button>
        )}

        {isChangeProgramAllowed && (
          <Button
            className="flex-1 whitespace-nowrap"
            data-testid={UpdateProgramPopupTestIds.SELECT_NEW_PROGRAM_BUTTON}
            disabled={isLoading || isProgramChangeDisabled}
            size="large"
            onClick={onSaveWithProgramClick}
          >
            Select New Program
          </Button>
        )}
      </ButtonGroupWrapper>
    </Box>
  );
};

UpdateProgramPopup.propTypes = {
  ...WithLoadingPropTypes,
  handlerProps: PropTypes.object.isRequired,
  message: PropTypes.string.isRequired,
  onRevert: PropTypes.func.isRequired,
  onAccept: PropTypes.func.isRequired,
  isRevertNeededAfterAccept: PropTypes.bool,
  isSuccessActionNeededAfterAccept: PropTypes.bool,
  isSaveWithoutProgramHidden: PropTypes.bool,
  loanOptionsSource: PropTypes.string,
};

export default withLoading(UpdateProgramPopup);
